import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useOpenCaseMutation } from '@/shared/api/endpoints';
import { selectHasRole } from '@/shared/api/sessionSlice';
import type { ModerationTargetKind } from '@/shared/api/types';
import { cn } from '@/shared/lib/cn';
import { MODERATORS } from '@/shared/lib/roles';
import { ApiError } from '@/shared/ui/ApiError';
import { Button } from '@/shared/ui/Button';
import { Alert, TextArea } from '@/shared/ui/primitives';

interface ReportButtonProps {
  kind: ModerationTargetKind;
  /** A user id, a course id or a lesson id. */
  id: string;
  className?: string;
}

/**
 * "Send to moderation": hands a user, a course or a lesson over for a decision.
 * Shown to admins and to root only. Sending is all an ordinary admin can do; what happens to
 * it is decided by an admin who has the permission.
 */
export function ReportButton({ kind, id, className }: ReportButtonProps) {
  const { t } = useTranslation(['moderation', 'common']);
  const isAdmin = useSelector(selectHasRole(...MODERATORS));
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [send, state] = useOpenCaseMutation();
  if (!isAdmin) return null;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const result = await send({ targetKind: kind, targetId: id, reason: reason.trim() });
    if ('data' in result) {
      setOpen(false);
      setReason('');
    }
  };

  return (
    <div className={cn('space-y-2', className)}>
      {open ? (
        <form
          className="space-y-2 rounded-xl border border-line bg-surface p-4"
          onSubmit={(event) => void onSubmit(event)}
        >
          <TextArea
            label={t('report.reason')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={3}
            maxLength={2000}
            required
          />
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={reason.trim().length < 3 || state.isLoading}>
              {t('report.send')}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              {t('report.cancel')}
            </Button>
          </div>
        </form>
      ) : (
        <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
          {t('report.button')}
        </Button>
      )}
      {state.isSuccess && !open ? (
        <Alert tone="ok" live>
          {t('report.sent')}
        </Alert>
      ) : null}
      <ApiError error={state.error} />
    </div>
  );
}
